import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  DndContext, DragOverlay, useDroppable, useDraggable,
  PointerSensor, useSensor, useSensors,
  closestCorners,
} from '@dnd-kit/core';
import { pmApi } from '../../api/pm.js';
import { useRealtime } from '../../hooks/useRealtime.js';
import { contrastText } from '../../utils/contrast.js';

export default function Board() {
  const { key } = useParams();
  const [project, setProject] = useState(null);
  const [board, setBoard] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [error, setError] = useState(null);
  // Track whether a drag actually moved (distance > 0) so the post-drag
  // pointerup/click on the card is suppressed and doesn't navigate.
  const didDragRef = useRef(false);

  useEffect(() => {
    pmApi.getProject(key).then((d) => setProject(d?.project));
  }, [key]);

  useEffect(() => {
    if (!project) return;
    pmApi.boards(project.id).then((d) => {
      const main = (d?.items || []).find((b) => b.is_default) || (d?.items || [])[0];
      if (main) loadBoard(main.id);
    });
    // eslint-disable-next-line
  }, [project]);

  const boardIdRef = useRef(null);
  function loadBoard(boardId) {
    boardIdRef.current = boardId;
    pmApi.getBoard(boardId).then((d) => setBoard(d?.board));
  }

  // Live board: when any issue's status changes (e.g. another user drags a
  // card, or a status edit on the issue detail page), the API emits
  // `issue:status:updated` to the project room. Re-fetch the board so the
  // card lands in its new column without a manual refresh.
  useRealtime({
    type: 'project',
    id: project?.id,
    events: {
      'issue:status:updated': () => {
        if (boardIdRef.current) loadBoard(boardIdRef.current);
      },
    },
  });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const allIssues = useMemo(() => {
    if (!board) return [];
    return board.columns.flatMap((c) => c.issues);
  }, [board]);

  const onDragStart = useCallback((e) => {
    setActiveId(e.active.id);
    didDragRef.current = false;
  }, []);
  const onDragMove = useCallback(() => { didDragRef.current = true; }, []);
  const onDragEnd = useCallback(async (e) => {
    setActiveId(null);
    if (!e.over || !board) return;
    const issueId = e.active.id;
    const overId = e.over.id;

    // overId is either a column id (string starting with 'col-') or another issue's id.
    const targetCol = board.columns.find((c) =>
      `col-${c.id}` === overId || c.issues.some((i) => i.id === overId)
    );
    if (!targetCol) return;

    const moved = allIssues.find((i) => i.id === issueId);
    if (!moved) return;
    const targetStatus = (targetCol.status_mapping || [])[0] || moved.status;
    if (moved.status === targetStatus) return;

    // Optimistic UI: move the card immediately, then persist.
    setBoard((prev) => moveIssue(prev, issueId, targetCol.id));
    try {
      await pmApi.setIssueStatus(issueId, targetStatus);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update status');
      // Reload from server to undo.
      const main = board.id;
      loadBoard(main);
    }
  }, [board, allIssues]);

  if (!project) return <div className="muted">Loading…</div>;
  if (!board) return <div className="muted">No board configured for this project.</div>;

  const draggingIssue = activeId ? allIssues.find((i) => i.id === activeId) : null;

  return (
    <section>
      <header className="page-header">
        <div>
          <h1>{project.name} · Board</h1>
          <p className="muted">{board.name} · drag cards between columns</p>
        </div>
        <Link to={`/pm/projects/${project.key}/issues/new`} className="btn primary">+ Create issue</Link>
      </header>
      {error && <div className="alert error">{error}</div>}

      <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={onDragStart} onDragMove={onDragMove} onDragEnd={onDragEnd}>
        <div
          className="kanban"
          data-testid="board-container"
          data-loaded="true"
          style={{ overflowX: 'auto' }}
        >
          <div data-testid="board" style={{ display: 'flex', gap: 12, minWidth: 'max-content' }}>
            {board.columns.map((col) => (
              <Column key={col.id} col={col} projectKey={project.key} didDragRef={didDragRef} />
            ))}
          </div>
        </div>
        <DragOverlay>{draggingIssue && <IssueCard issue={draggingIssue} projectKey={project.key} dragging />}</DragOverlay>
      </DndContext>
    </section>
  );
}

function moveIssue(board, issueId, targetColId) {
  if (!board) return board;
  const issue = board.columns.flatMap((c) => c.issues).find((i) => i.id === issueId);
  if (!issue) return board;
  const targetCol = board.columns.find((c) => c.id === targetColId);
  const targetStatus = (targetCol?.status_mapping || [])[0] || issue.status;
  return {
    ...board,
    columns: board.columns.map((c) => {
      if (c.id === targetColId) {
        return { ...c, issues: [...c.issues, { ...issue, status: targetStatus }] };
      }
      return { ...c, issues: c.issues.filter((i) => i.id !== issueId) };
    }),
  };
}

function Column({ col, projectKey, didDragRef }) {
  const { isOver, setNodeRef } = useDroppable({ id: `col-${col.id}` });
  const overWip = col.over_wip || (col.wip_limit && col.issues.length > col.wip_limit);
  return (
    <div
      ref={setNodeRef}
      className={`kcol ${isOver ? 'kcol-over' : ''} ${overWip ? 'kcol-over-wip wip-exceeded' : ''}`}
      data-testid="board-column"
      data-column-status={col.name}
      data-loaded="true"
    >
      <header data-testid="column-header" className={overWip ? 'wip-exceeded' : ''}>
        <span className="kcol-name" style={{ borderColor: col.color || '#94a3b8' }}>{col.name}</span>
        <span className={`kcol-count ${overWip ? 'over' : ''}`}>
          {col.issues.length}{col.wip_limit ? `/${col.wip_limit}` : ''}
        </span>
      </header>
      <div className="kcol-body">
        {col.issues.map((issue) => (
          <DraggableIssue key={issue.id} issue={issue} projectKey={projectKey} didDragRef={didDragRef} />
        ))}
        {col.issues.length === 0 && <div className="kcol-empty muted">Drop issues here</div>}
      </div>
    </div>
  );
}

function DraggableIssue({ issue, projectKey, didDragRef }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: issue.id });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : {};
  const fullKey = issue.full_key || `${projectKey}-${issue.issue_no}`;
  // dnd-kit's `attributes` add role="button" + tabIndex for keyboard dragging,
  // but this wrapper contains the card's <Link> (anchor) — a button nested
  // around a link trips axe's `nested-interactive`. Pointer dragging works via
  // `listeners` alone, so drop the interactive role/tabindex and keep the Link
  // as the single interactive control.
  const { role: _role, tabIndex: _tabIndex, ...dragAttrs } = attributes;
  return (
    <div
      ref={setNodeRef}
      style={{ ...style, opacity: isDragging ? 0.4 : 1 }}
      data-testid="issue-card"
      data-issue-key={fullKey}
      data-issue-status={issue.status}
      {...dragAttrs}
      {...listeners}
    >
      <IssueCard issue={issue} projectKey={projectKey} didDragRef={didDragRef} />
    </div>
  );
}

function IssueCard({ issue, projectKey, dragging, didDragRef }) {
  const fullKey = issue.full_key || `${projectKey}-${issue.issue_no}`;
  return (
    <Link
      to={`/pm/issues/${fullKey}`}
      className={`issue-card type-${issue.type} prio-${issue.priority} ${dragging ? 'dragging' : ''}`}
      onClick={(e) => {
        // Suppress navigation if a drag just occurred (dragging overlay) or if
        // the didDragRef flag was set by onDragMove during this interaction.
        if (dragging || didDragRef?.current) e.preventDefault();
      }}
    >
      <header>
        <span className={`issue-type t-${issue.type}`}>{issue.type[0].toUpperCase()}</span>
        <code>{fullKey}</code>
        <span className={`prio-dot prio-${issue.priority}`} title={issue.priority} />
      </header>
      <div className="issue-title">{issue.title}</div>
      <footer>
        {issue.epic_title && <span className="chip" style={{ background: issue.epic_color || '#e0e7ff', color: contrastText(issue.epic_color || '#e0e7ff') }}>{issue.epic_title}</span>}
        {issue.story_points != null && <span className="chip">{Number(issue.story_points)} pts</span>}
        {issue.assignee_name && <span className="avatar tiny">{issue.assignee_name.charAt(0).toUpperCase()}</span>}
        <span data-testid="status-badge" className={`chip status-${issue.status}`} style={{ marginLeft: 'auto' }}>{issue.status}</span>
      </footer>
    </Link>
  );
}
